import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { resolveEntitlement } from '../utils/grandfathering';

export const consentLimitService = {
  // Check if clinic can create more consents
  async checkConsentLimits(clinicId) {
    try {
      // Get clinic subscription info
      const clinicDoc = await getDoc(doc(db, 'clinics', clinicId));
      if (!clinicDoc.exists()) {
        throw new Error('Clinic not found');
      }
      
      const clinicData = clinicDoc.data();
      const subscription = clinicData.subscription || {};
      
      const entitlement = resolveEntitlement(subscription, new Date(), clinicId);
      const plan = entitlement.planKey;
      const monthlyLimit = entitlement.monthlyConsentLimit;
      
      // If unlimited (-1), allow creation
      if (monthlyLimit === -1) {
        return {
          canCreate: true,
          unlimited: true,
          used: 0,
          limit: -1,
          remaining: -1,
          percentage: 0,
          atLimit: false,
          nearLimit: false,
          plan: plan,
          planName: entitlement.planName
        };
      }
      
      // Calculate current month's usage from ALL sources (query by clinicId only, filter by month in memory so we don't rely on composite indexes)
      const thisMonth = new Date();
      const startOfMonth = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1);
      
      let totalMonthlyConsents = 0;
      
      const isInThisMonth = (createdAt) => {
        if (!createdAt) return false;
        const d = createdAt && typeof createdAt.toDate === 'function' ? createdAt.toDate() : new Date(createdAt);
        return d >= startOfMonth;
      };
      
      // 1. Main consents collection
      try {
        const consentsQuery = query(collection(db, 'consents'), where('clinicId', '==', clinicId));
        const consentsSnapshot = await getDocs(consentsQuery);
        consentsSnapshot.forEach((doc) => {
          if (isInThisMonth(doc.data().createdAt)) totalMonthlyConsents += 1;
        });
      } catch (error) {
        console.log('Error checking main consents collection:', error);
      }
      
      // 2. aiConsents collection (where AI-generated consents are stored)
      try {
        const aiConsentsQuery = query(collection(db, 'aiConsents'), where('clinicId', '==', clinicId));
        const aiConsentsSnapshot = await getDocs(aiConsentsQuery);
        aiConsentsSnapshot.forEach((doc) => {
          const row = doc.data();
          if (row.status === 'draft') return;
          if (isInThisMonth(row.createdAt)) totalMonthlyConsents += 1;
        });
      } catch (error) {
        console.log('Error checking aiConsents collection:', error);
      }
      
      // 3. Clinic subcollection
      try {
        const clinicConsentsRef = collection(db, 'clinics', clinicId, 'consents');
        const clinicConsentsSnapshot = await getDocs(clinicConsentsRef);
        clinicConsentsSnapshot.forEach((doc) => {
          if (isInThisMonth(doc.data().createdAt)) totalMonthlyConsents += 1;
        });
      } catch (error) {
        console.log('Error checking clinic subcollection:', error);
      }
      
      const percentage = (totalMonthlyConsents / monthlyLimit) * 100;
      const remaining = Math.max(0, monthlyLimit - totalMonthlyConsents);
      
      return {
        canCreate: totalMonthlyConsents < monthlyLimit,
        unlimited: false,
        used: totalMonthlyConsents,
        limit: monthlyLimit,
        remaining: remaining,
        percentage: Math.round(percentage),
        atLimit: totalMonthlyConsents >= monthlyLimit,
        nearLimit: percentage >= 80 && percentage < 100,
        plan: plan,
        planName: entitlement.planName
      };
    } catch (error) {
      console.error('Error checking consent limits:', error);
      throw error;
    }
  },

  // Enforce consent limits - throws error if limit reached
  async enforceConsentLimits(clinicId) {
    const limits = await this.checkConsentLimits(clinicId);
    
    if (!limits.canCreate) {
      throw new Error(
        `Monthly consent limit reached (${limits.used}/${limits.limit}). ` +
        `Please upgrade your plan or wait until next month to create more consents. ` +
        `Current plan: ${limits.planName}`
      );
    }
    
    return limits;
  },

  // Get upgrade suggestions based on current usage
  getUpgradeSuggestions(limits) {
    if (limits.unlimited) {
      return null;
    }
    
    const suggestions = [];
    
    if (limits.percentage >= 90) {
      suggestions.push({
        priority: 'high',
        message: 'You are very close to your monthly limit. Consider upgrading to avoid interruptions.',
        action: 'upgrade_now'
      });
    } else if (limits.percentage >= 80) {
      suggestions.push({
        priority: 'medium',
        message: 'You are approaching your monthly limit. Consider upgrading for more flexibility.',
        action: 'consider_upgrade'
      });
    }
    
    // Suggest specific plans based on current usage
    if ((limits.plan === 'starter' || limits.plan === 'starter_v2') && limits.percentage >= 70) {
      suggestions.push({
        priority: 'medium',
        message: `Upgrade to Professional for 500 consents/month (currently ${limits.limit})`,
        action: 'upgrade_to_professional',
        plan: 'professional',
        price: '£39.99/month'
      });
    } else if ((limits.plan === 'professional' || limits.plan === 'growthpro') && limits.percentage >= 70) {
      suggestions.push({
        priority: 'medium',
        message: 'Upgrade to Premium for 1,500 consents/month',
        action: 'upgrade_to_premium',
        plan: 'premium',
        price: '£69.99/month'
      });
    }
    
    return suggestions;
  },

  // Check if user should see a warning
  shouldShowWarning(limits) {
    return limits.nearLimit || limits.atLimit;
  },

  // Get warning message
  getWarningMessage(limits) {
    if (limits.atLimit) {
      return {
        type: 'error',
        title: 'Monthly Limit Reached',
        message: `You have reached your monthly consent limit (${limits.used}/${limits.limit}). Please upgrade your plan or wait until next month.`,
        action: 'upgrade_required'
      };
    }
    
    if (limits.nearLimit) {
      return {
        type: 'warning',
        title: 'Approaching Limit',
        message: `You are approaching your monthly consent limit (${limits.used}/${limits.limit}). Consider upgrading your plan for more flexibility.`,
        action: 'consider_upgrade'
      };
    }
    
    return null;
  }
};
